import { Parser } from '~/api';
import RequestService from './request';

export interface ServerSplit {
    id: string;
    uuid: string;
    identifier: string;
    name: string;
    description: string | null;
    egg_id: string | number;
    egg_name: string | null;
    status: string | null;
    is_installed: boolean;
    sync_subusers: boolean;
    under_minimum: boolean;
    resources: Record<string, number | null>;
    allocation: { ip: string; port: number } | null;
    created_at: string | null;
}

export interface SplitterEgg {
    id: string | number;
    name: string;
    description?: string | null;
    // Egg resource requirements, as described by EggSwitchRequirement::describe.
    requirements?: {
        minimum: { cpu: number | null; memory: number | null; disk: number | null };
        recommended: { cpu: number | null; memory: number | null; disk: number | null };
    };
    meets_minimum?: boolean;
    meets_recommended?: boolean;
    requires_node_change?: boolean;
}

export interface SplitResourceAvailability {
    total: number | null;
    assigned: number;
    remaining: number | null;
    reserved: number;
    max_assignable: number | null;
}

export interface SplitAvailability {
    resources: Record<string, SplitResourceAvailability>;
    // A null limit/available means the root's split_limit is uncapped (resource-bound only).
    splits: { limit: number | null; used: number; available: number | null };
    reserved: { cpu: number; memory: number; disk: number };
    include_disk_usage: boolean;
    main_under_minimum: boolean;
}

export interface CreateSplitPayload {
    name: string;
    description?: string;
    egg_id: string | number;
    resources: Record<string, number>;
    environment?: Record<string, string>;
    sync_subusers?: boolean;
    reboot_main_server?: boolean;
}

export interface UpdateSplitPayload {
    name: string;
    description?: string;
    resources: Record<string, number>;
    sync_subusers?: boolean;
}

class SplitterService {
    getAll(): Promise<{ data: Array<{ attributes: ServerSplit }> }> {
        return RequestService.get('/servers/:server/splitter');
    }

    availability(): Promise<{ data: SplitAvailability }> {
        return RequestService.get('/servers/:server/splitter/availability');
    }

    getEggs(): Promise<ListResponse> {
        return RequestService.get('/servers/:server/splitter/eggs').then(Parser.parse);
    }

    getEggVariables(eggId: string | number): Promise<ListResponse> {
        return RequestService.get(`/servers/:server/splitter/eggs/${eggId}/variables`).then(Parser.parse);
    }

    validateVariables(eggId: string | number, environment: Record<string, string>): Promise<{ errors: Record<string, string> }> {
        return RequestService.post(`/servers/:server/splitter/eggs/${eggId}/validate`, { environment });
    }

    create(data: CreateSplitPayload): Promise<{ attributes: Record<string, unknown> }> {
        return RequestService.post('/servers/:server/splitter', data);
    }

    update(identifier: string, data: UpdateSplitPayload): Promise<{ attributes: ServerSplit }> {
        return RequestService.put(`/servers/:server/splitter/${identifier}`, data);
    }

    promote(identifier: string): Promise<{ attributes: ServerSplit }> {
        return RequestService.post(`/servers/:server/splitter/${identifier}/promote`);
    }

    delete(identifier: string, rebootMain: boolean): Promise<void> {
        return RequestService.delete(`/servers/:server/splitter/${identifier}`, { data: { reboot_main_server: rebootMain } });
    }
}

export default new SplitterService();
